import { ALERT_LEVEL, getAlertLevelIndex } from "@const/alerting.constants";
import { AlertHistory, AlertHistoryDTO, AlertLevel, AlertOverlimitItemsDTO } from "@dto/alerting.dto";
import { OverlimitItem } from "@dto/architecture.dto";
import { Metric } from "@dto/pageResponse.dto";
import { DateUtil } from "@dto/util/DateUtil";
import { UnitUtil } from "@dto/util/UnitUtil";
import { CustomTagProps } from '@rc-component/select/es/BaseSelect';
import { Input, Typography } from "antd";
import { RangePickerProps } from "antd/es/date-picker";
import { ColumnFilterItem } from "antd/es/table/interface";
import axios, { AxiosError } from "axios";
import dayjs from "dayjs";
import { Dispatch, FC, SetStateAction, startTransition, useEffect, useMemo, useState } from "react";
import XmRangeDatePicker from "src/component/dataTimePicker/XmRangeDatePicker";
import { XmListy } from "src/component/list/XmListy";
import { XmModal } from "src/component/modal/XmModal";
import OptionDetail from "src/component/select/OptionDetail";
import { XmSelect } from "src/component/select/XmSelect";
import { makeItemLabelRoot } from "src/component/selectItems/children/Items";
import { XmSpinner } from "src/component/spinner/XmSpinner";
import { XmTableScope } from "src/component/table/XmTable";
import { ColProps } from "src/component/table/tableTypes";
import { API_URL } from "src/data/Api";
import { Log } from "src/service/log.service";
import { TextUtil } from "src/util/TextUtil";
import { getApi } from "src/util/apiCalls";
import { nameof } from "ts-simple-nameof";

interface HistoryFilters {
  readonly alertGroupType: ColumnFilterItem[];
  readonly alertGroup: ColumnFilterItem[];
  readonly metric: ColumnFilterItem[];
  readonly item: ColumnFilterItem[];
  readonly userGroup: ColumnFilterItem[];
  readonly target: ColumnFilterItem[];
}

const EMPTY_HISTORY_FILTERS: HistoryFilters = { alertGroupType: [], alertGroup: [], metric: [], item: [], userGroup: [], target: [] };

const toFilterItem = (text: string): ColumnFilterItem => ({ text, value: text });

const byLabel = (a: { label: string; }, b: { label: string; }): number => a.label.localeCompare(b.label);

function uniqueBy<T>(values: readonly T[], key: (value: T) => string): T[] {
  const seen = new Map<string, T>();
  values.forEach(value => {
    const k = key(value);
    if (!seen.has(k)) seen.set(k, value);
  });
  return [...seen.values()];
}

function buildHistoryFilters(hist: readonly AlertHistory[]): HistoryFilters {
  return {
    alertGroupType: uniqueBy(hist, ah => ah.alerting_group_alert_type)
      .sort(alertGroupTypeSorter)
      .map(ah => toFilterItem(ah.alerting_group_alert_type)),
    alertGroup: uniqueBy(hist, ah => ah.alerting_group_id)
      .sort(alertGroupSorter)
      .map(ah => toFilterItem(ah.alerting_group_label)),
    metric: uniqueBy(hist, ah => ah.metric.label)
      .sort(metricSorter)
      .map(ah => toFilterItem(ah.metric.label)),
    item: uniqueBy(hist.flatMap(ah => ah.items), i => i.label)
      .sort(byLabel)
      .map(i => toFilterItem(i.label)),
    userGroup: uniqueBy(hist, ah => ah.group_label)
      .sort(userGroupSorter)
      .map(ah => toFilterItem(ah.group_label)),
    target: [...new Set(hist.flatMap(ah => ah.targets))]
      .sort((a, b) => a.localeCompare(b))
      .map(toFilterItem),
  };
}

function sortHistoryInPlace(hist: AlertHistory[]): AlertHistory[] {
  hist.sort(triggeredSorter).reverse();
  hist.forEach(ah => {
    ah.targets.sort((a, b) => a.length - b.length);
    ah.items.sort((a, b) => a.label.length - b.label.length);
  });
  return hist;
}

const historyRowKey = (record: AlertHistory): string => record.alerting_history_id;

const getHistoryItemLabel = (item: OverlimitItem, metric: Metric, stringOnly: boolean = false) => {
  const isANumber = typeof item?.overlimit === 'number' || typeof item?.overlimit === 'string' && !isNaN(Number(item.overlimit));
  const overlimitPart = isANumber && item?.overlimit ? ` [${metric.unit !== "count" ? UnitUtil.formatSI(Number(item.overlimit), 2, false, metric) : item.overlimit}]` : item?.overlimit ? ` [${item.overlimit}]` : '';
  return !stringOnly ? <span>{makeItemLabelRoot(item, item.url ? true : false)}{overlimitPart}</span> : item.label + (item.root_parent ? ` (${item.root_parent.label})` : '') + overlimitPart;
};

const AlertingHistoryModal: FC<{ labels: OverlimitItem[], isModalOpen: boolean, setIsModalOpen: Dispatch<SetStateAction<boolean>>, metric: Metric, count: number; }> = ({ labels, isModalOpen, setIsModalOpen, metric, count }) => {

  const [searchTerm, setSearchTerm] = useState("");


  // Filtered list based on search term
  const filteredData = useMemo(
    () => labels.filter(label =>
      (getHistoryItemLabel(label, metric, true) as string).toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [labels, metric, searchTerm]
  );

  const [listHeight] = useState<number>(() => Math.round(window.innerHeight * 0.6));

  const closeModal = () => {
    setIsModalOpen(false);
    setSearchTerm("");
  };

  return <XmModal
    title={`Alert History Items (${labels.length})${count && count - labels.length > 0 ? ` (${count - labels.length} deleted)` : ''}`}
    open={isModalOpen}
    onCancel={closeModal}
    onOk={closeModal}
    cancelButtonProps={{ style: { display: 'none' } }}
  >
    <Input.Search
      placeholder="Search..."
      allowClear
      value={searchTerm}
      onChange={e => setSearchTerm(e.target.value)}
      style={{ marginBottom: 16 }}
    />

    <XmListy<OverlimitItem>
      virtual
      height={listHeight}
      items={filteredData}
      rowKey={i => i.item_id}
      itemRender={i => getHistoryItemLabel(i, metric)}
    />
  </XmModal>;
};

const AlertingHistoryItems: FC<{ alerting_history_id: string, metric: Metric, count: number; }> = ({ alerting_history_id, metric, count }) => {
  const [items, setItems] = useState<OverlimitItem[]>([]);
  const [showLoader, setShowLoader] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchItems = async () => {
    if (showLoader) return; // Prevent multiple fetches
    setShowLoader(true);
    try {
      if (!items?.length) {
        const resp = await axios.get<AlertOverlimitItemsDTO>(API_URL.ALERTING + '/history/items/' + alerting_history_id);
        setItems(resp.data.data);
      }
      setIsModalOpen(true);
    } catch (error) {
      Log.error('Failed to fetch alert history items', error);
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <>
      <AlertingHistoryModal
        labels={items}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        metric={metric}
        count={count}
      />
      <span className="xm-lazy-load-items" onClick={fetchItems}>{showLoader ? <XmSpinner size="small" style={{ zIndex: 99 }} /> : <Typography.Link>{`${count}...`}</Typography.Link>}</span>
    </>);
};

export const AlertingHistory: FC = props => {
  const [tscope] = useState(() => new XmTableScope<AlertHistory>);
  const [history, setHistory] = useState<AlertHistory[]>([]);
  const [filters, setFilters] = useState<HistoryFilters>(EMPTY_HISTORY_FILTERS);
  const [loading, setLoading] = useState(true);
  const [state] = useState<{ start: Date, end: Date, aborter: AbortController; }>(() => {
    const start = new Date();
    start.setDate(start.getDate() - 1);
    const end = new Date();
    return { aborter: null, start, end };
  });
  const severityFilter = useMemo<ColumnFilterItem[]>(() => Object.entries(ALERT_LEVEL).map(value => ({ text: value[0], value: value[1] })), []);

  useEffect(() => {
    updateHistory();
    return () => {
      state.aborter.abort();
    };
  }, []);

  function updateHistory() {
    state.aborter?.abort();
    const aborter = new AbortController();
    state.aborter = aborter;
    startTransition(() => setLoading(true));
    getApi<AlertHistoryDTO>(API_URL.ALERTING + '/history', aborter.signal,
      { params: { from: state.start, to: state.end } }).then(resp => {
        const hist = sortHistoryInPlace(resp.data.data);
        startTransition(() => {
          setHistory(hist);
          setFilters(buildHistoryFilters(hist));
          setLoading(false);
        });
      }, (reason: AxiosError) => {
        if (aborter.signal.aborted) return;
        if (!axios.isCancel(reason)) Log.error('Failed to load alert history!', reason);
        setLoading(false);
      });
  }

  const cols = useMemo((): ColProps<AlertHistory>[] => [
    {
      key: 'time',
      title: 'Time',
      minWidth: '8em',
      dataIndex: path(t => t.triggered),
      //defaultSortOrder: 'descend',
      sorter: triggeredSorter,
      render: (value, record) => DateUtil.getShortDateTime(value)
    },
    {
      key: 'alertType',
      title: 'Alert Type',
      minWidth: '12em',
      dataIndex: path(t => t.alerting_group_alert_type),
      sorter: alertGroupTypeSorter,
      filters: filters.alertGroupType,
      filterSearch: true,
      onFilter: (value: string, record) => record.alerting_group_alert_type === value,
      render: (value, record) => TextUtil.trimEnd(value, 64)
    },
    {
      key: 'alertGroup',
      title: 'Alerting Group',
      minWidth: '12em',
      dataIndex: path(t => t.alerting_group_label),
      sorter: alertGroupSorter,
      filters: filters.alertGroup,
      filterSearch: true,
      onFilter: (value: string, record) => record.alerting_group_label === value,
      render: (value, record) => TextUtil.trimEnd(value, 64)
    },
    {
      key: 'severity',
      title: 'Severity',
      minWidth: '7em',
      dataIndex: path(t => t.level),
      sorter: (a, b) => getAlertLevelIndex(a.level) - getAlertLevelIndex(b.level),
      filters: severityFilter,
      onFilter: (value: AlertLevel, record) => record.level === value,
    },
    {
      key: 'metric',
      title: 'Metric',
      minWidth: '10em',
      dataIndex: path(t => t.metric.label),
      sorter: metricSorter,
      filters: filters.metric,
      filterSearch: true,
      onFilter: (value, record) => record.metric.label === value,
      render: (value, record) => {
        const m = record.metric;
        const prefix = m.defaultPrefix || UnitUtil.sizeUnit(0, m.shortcut, m.unit).unitSizePrefix;
        const prefixShortcut = (prefix || '') + m.shortcut;
        return <OptionDetail label={record.metric.label} detail={prefixShortcut} />;
      }
    },
    {
      key: 'limit',
      title: 'Limit',
      dataIndex: path(t => t.limit),
      sorter: (a, b) => a.limit - b.limit,
      render: (value, record) => UnitUtil.roundValue(UnitUtil.convertSImetric(record.limit, record.metric), 1, record.metric.unit, record.metric)
    },
    {
      key: 'items',
      title: 'Items',
      minWidth: '12em',
      className: 'xm-col-no-pad',
      dataIndex: path(t => t.items.length),
      noTooltip: true,
      render: (value, record) => <AlertingHistoryItems alerting_history_id={record.alerting_history_id} metric={record.metric as Metric} count={record.count} />,
    },
    {
      key: 'groupShared',
      title: 'Group Shared',
      minWidth: '10em',
      dataIndex: path(t => t.group_label),
      sorter: userGroupSorter,
      filters: filters.userGroup,
      filterSearch: true,
      onFilter: (value, record) => record.group_label === value
    },
    {
      key: 'targets',
      width: '15em',
      // className: 'xm-col-no-pad',
      title: 'Targets',
      dataIndex: path(t => t.targets.length),
      filters: filters.target,
      filterSearch: true,
      noTooltip: true,
      onFilter: (value, record) => record.targets.some(t => t === value),
      render: (value, record) => <XmSelect ignoreReadonly classNames={{ popup: { root: "xm-select-readonly" } }} mode="multiple" maxTagCount="responsive" options={record.targets.map(t => ({ value: t, label: t }))}
        style={{ width: '100%' }} value={record.targets}
        menuItemSelectedIcon={null} removeIcon={''}
      />
    }
  ], [filters, severityFilter]);

  const handleRangeChange: RangePickerProps['onChange'] = (values) => {
    state.start = values[0].toDate();
    state.end = values[1].toDate();
    updateHistory();
  };

  return <div className="xm-alert-history">
    <div className="xm-alert-history-range">
      Show alerts within time range: <XmRangeDatePicker
        allowClear={false}
        defaultValue={[dayjs(state.start), dayjs(state.end)]}
        onChange={handleRangeChange}
      />
    </div>
    <tscope.XmTable
      dataSource={history}
      rowKey={historyRowKey}
      columns={cols}
      loading={loading}
      sticky
      fillHeight
    ></tscope.XmTable>
  </div>;
};

// function ItemsTagRender(items: OverlimitItem[], useOverlimit = false, metric?: Metric) {
//   return (props: Readonly<CustomTagProps>) => SelectItemTagRender(props, items, useOverlimit, metric);
// }

export function SelectItemTagRender(props: Readonly<CustomTagProps>, items?: OverlimitItem[], useOverlimit = false, metric?: Metric) {
  const item = items?.find(i => i.item_id === props.value);
  const isANumber = typeof item?.overlimit === 'number' || typeof item?.overlimit === 'string' && !isNaN(Number(item.overlimit));
  const fallback = props.label ?? props.value;
  return <span className="ant-select-selection-item">{useOverlimit && item?.overlimit ? item?.label + ` [${isANumber && metric?.unit !== "count" ? UnitUtil.formatSI(Number(item?.overlimit), 2, false, metric) : item.overlimit}]` : item?.label || fallback}</span>;
}

function triggeredSorter(a: AlertHistory, b: AlertHistory) {
  return a.triggered.localeCompare(b.triggered);
}

function alertGroupSorter(a: AlertHistory, b: AlertHistory) {
  return a.alerting_group_label.localeCompare(b.alerting_group_label);
}

function alertGroupTypeSorter(a: AlertHistory, b: AlertHistory) {
  return a.alerting_group_alert_type.localeCompare(b.alerting_group_alert_type);
}

function metricSorter(a: AlertHistory, b: AlertHistory) {
  return a.metric.label.localeCompare(b.metric.label);
}

function userGroupSorter(a: AlertHistory, b: AlertHistory) {
  return a.group_label?.localeCompare(b.group_label);
}

function path(selector: (props: AlertHistory) => unknown) {
  return nameof<AlertHistory>(selector).split('.');
}